import { FixedColorArrow, Text, TextSize } from 'components'
import React, { MutableRefObject, ReactNode, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { Fonts, Weight } from '../typography/text/index'
import { sampleAccordionData } from './mock'

interface CustomProps {
  className?: string
  children?: JSX.Element
  accordionContent?: {
    title: string | JSX.Element
    content: string | ReactNode | ReactNode[]
  }[]
  currentAccordion?: string
  setCurrentAccordion?: string
  setBodyHeight?: string
  bodyHeight?: string
  analyticsClickEvent?: (label: string, external?: boolean | undefined) => void
}

type Props = CustomProps & React.HTMLProps<HTMLDivElement>

const Wrapper = styled.div`
  display: flex;
  gap: 75px;
  ${({ theme }) => theme.mediaWidth.Mobile`
    flex-direction: column;
    gap: 0;
  `};
`
const AccordionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  ${({ theme }) => theme.mediaWidth.Mobile`
    width: 100%;
  `};
`

const Item = styled.div`
  margin-bottom: 20px;
`

const AccordionContent = styled.div`
  margin: 0;
  height: auto;
  color: ${({ theme }) => theme.secondaryText};
  font-family: ${Fonts.DMSans};
  font-size: ${TextSize.small};
  line-height: 26px;
`
const Title = styled.div`
  display: flex;
  cursor: pointer;
  color: ${({ theme }) => theme.text1};
  justify-content: space-between;
  box-sizing: border-box;
  min-height: 50px;
  font-size: ${TextSize.lOne};
  gap: 10px;
  ${({ theme }) => theme.mediaWidth.Mobile`
      font-size: ${TextSize.nav};
  `}
`
const Body = styled.div<{ active: boolean; bodyHeight: number }>`
  display: block;
  position: relative;
  margin: 0;
  height: 0;
  overflow: hidden;
  transition: height 0.5s;

  ${({ active, bodyHeight }) =>
    active &&
    css`
      height: ${bodyHeight}px;
    `}
`

const StyledTitleText = styled(Text)`
  font-size: ${TextSize.four};
  font-weight: ${Weight.Bold};
  max-width: 90%;
`

export const Accordion: React.FC<Props> = ({
  accordionContent = sampleAccordionData,
  className,
  analyticsClickEvent,
}) => {
  const [currentAccordion, setCurrentAccordion] = useState<number | null>(null)
  const [bodyHeight, setBodyHeight] = useState(0)

  const refs: MutableRefObject<typeof Title | null>[] = Array.from(
    { length: accordionContent.length },
    () => useRef(null),
  )
  const accordionHalf = Math.round(accordionContent.length / 2)
  const firstPart = accordionContent.slice(0, accordionHalf)
  const secondPart = accordionContent.slice(accordionHalf)

  return (
    <Wrapper className={className}>
      <AccordionsColumn>
        {firstPart.map(({ title, content }, i) => {
          return (
            <Item key={`${title}-${i}`}>
              <Title
                onClick={() => {
                  setCurrentAccordion(currentAccordion === i ? null : i)
                  setBodyHeight(
                    refs[i]?.current?.clientHeight ? (refs[i]?.current?.clientHeight || 0) + 20 : 0,
                  )
                  if (analyticsClickEvent) {
                    analyticsClickEvent(`${title} accordion item expanded`)
                  }
                }}
              >
                <StyledTitleText>{title}</StyledTitleText>
                <FixedColorArrow size={10} direction={currentAccordion === i ? 'top' : 'bottom'} />
              </Title>
              <Body active={currentAccordion === i} bodyHeight={bodyHeight}>
                <AccordionContent ref={refs[i] as MutableRefObject<null>}>
                  {content}
                </AccordionContent>
              </Body>
            </Item>
          )
        })}
      </AccordionsColumn>
      <AccordionsColumn>
        {secondPart.map(({ title, content }, i) => {
          const index = i + accordionHalf
          return (
            <Item key={`${title}-${index}`}>
              <Title
                onClick={() => {
                  setCurrentAccordion(currentAccordion === index ? null : index)
                  setBodyHeight(
                    refs[index]?.current?.clientHeight
                      ? (refs[index]?.current?.clientHeight || 0) + 20
                      : 0,
                  )
                  if (analyticsClickEvent) {
                    analyticsClickEvent(`${title} accordion item expanded`)
                  }
                }}
              >
                <StyledTitleText>{title}</StyledTitleText>
                <FixedColorArrow
                  size={10}
                  direction={currentAccordion === index ? 'top' : 'bottom'}
                />
              </Title>
              <Body active={currentAccordion === index} bodyHeight={bodyHeight}>
                <AccordionContent ref={refs[index] as MutableRefObject<null>}>
                  {content}
                </AccordionContent>
              </Body>
            </Item>
          )
        })}
      </AccordionsColumn>
    </Wrapper>
  )
}
